import React, { FC, useEffect, useRef, useState } from 'react';
import { Col, Divider, Row, Space, Spin, Table, Tag, Typography } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { ControlOutlined } from '@ant-design/icons';
import { UserAvatar } from '../../../../components/atoms/UserAvatar';
import { ClockCircleOutlined } from '@ant-design/icons';
import { fullMark, sectionMark } from '../PerformanceResults/PerformanceResultsPage';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import { useConnection } from '../../../../connection/Application';
import { useParams } from 'react-router';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { useHistory } from 'react-router-dom';
import Progress from 'antd/lib/progress';
import Link from 'antd/lib/typography/Link';
import { useQueryParam } from 'use-query-params';
const { Text } = Typography;

export const UserPerformanceResultsPage: FC = () => {
  const [teams, setTeams] = useState<any[]>([]);
  const [user, setUser] = useState<{
    userImage: string;
    userName: string;
    goalHolderId: number;
    id: number;
  }>({
    userImage: '',
    userName: '',
    goalHolderId: 0,
    id: 0,
  });
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const cycleRef = useRef(cycle);
  const [loading, setLoading] = useState<boolean>(false);
  const params = useParams<{ id: string }>();
  const connection = useConnection();
  const history1 = useHistory();

  const PerformanceResultsColumns = [
    {
      title: 'Component',
      dataIndex: 'type',
      key: 'type',
      // eslint-disable-next-line react/display-name
      render: (title: Text, record: any) => (
        <Link
          onClick={() => {
            history1.push(record?.link ?? '');
          }}>
          {title}
        </Link>
      ),
    },
    {
      title: 'Progress',
      dataIndex: 'approved',
      key: 'approved',
      width: '250px',
      // eslint-disable-next-line react/display-name
      render: (title: Text, record: any) => (
        <Progress
          percent={
            +((record?.approved / (record?.pending + record?.approved)) * 100 ?? 0).toFixed(0)
          }
        />
      ),
    },
    {
      title: 'Weight',
      dataIndex: 'percentage',
      key: 'percentage',
      width: '150px',
    },
    {
      title: 'Total Marks',
      dataIndex: 'answer',
      key: 'answer',
      width: '150px',
      // eslint-disable-next-line react/display-name
      render: (title: Text, record: any) => (
        <Typography>
          {typeof record?.answer === 'string'
            ? record?.answer
            : `${(record?.answer ?? 0).toFixed(2)}%`}
        </Typography>
      ),
    },
  ];

  useEffect(() => {
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  useEffect(() => {
    if (cycle) {
      setLoading(true);
      connection
        .get(`users/overall-score/${params.id}/${cycle}`)
        .then((res: any) => {
          setTeams(res.response.teams ?? []);
          setUser(res.response.user);
        })
        .catch(() => setLoading(false))
        .finally(() =>
          setTimeout(() => {
            setLoading(false);
          }, 600),
        );
    }
  }, [connection, cycle, params.id]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title={`${user?.userName ?? ''} Performance Result`}
        subTitle="View performance result details"
        onBack={() => history1.goBack()}
        breadcrumb={{
          items: [
            {
              path: '',
              title: `Performance Result / ${user?.userName ?? ''}`,
            },
          ],
        }}
      />
      <WebUxPageContent>
        <WebUxPageItem>
          <FlexBox>
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => setCycle(value, 'replace')}
              data={cycles}
            />
          </FlexBox>
          <Spin spinning={loading}>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Space size={24} align={'start'}>
                  <UserAvatar type={'team'} size={80} src={user.userImage} />
                  <Space direction={'vertical'}>
                    <Space direction={'vertical'} size={0}>
                      <Text>Final Score</Text>
                      <Typography.Title>
                        {teams.find(
                          (y) =>
                            typeof y.supervisorFeedBack?.answer === 'string' ||
                            typeof y.peerReviews?.answer === 'string' ||
                            typeof y.internalCustomers?.answer === 'string' ||
                            typeof y.teamPerformance?.answer === 'string',
                        ) ? (
                          <Tag icon={<ClockCircleOutlined />} color="default">
                            Pending
                          </Tag>
                        ) : (
                          `${fullMark(teams).toFixed(2)}%`
                        )}
                      </Typography.Title>
                      {/*<Tag color={e?.teamType?.color}>{e?.teamType?.name}</Tag>*/}
                    </Space>
                    {/*<Typography.Paragraph>{user?.description}</Typography.Paragraph>*/}
                  </Space>
                </Space>
              </div>
            </div>
            <Divider style={{ marginTop: 0, marginBottom: 5 }} />
            <Col>
              {teams.find((y) => y.supervisorFeedBack !== undefined) !== undefined && (
                <Row style={{ marginBottom: 10 }}>
                  <Space size={20}>
                    <Row style={{ width: 300 }}>
                      <div style={{ width: 150 }}>
                        <Text>Supervisor Feedback</Text>
                      </div>
                    </Row>
                    <Text>
                      {teams.find((y) => typeof y.supervisorFeedBack?.answer === 'string') ? (
                        <Tag icon={<ClockCircleOutlined />} color="default">
                          Pending
                        </Tag>
                      ) : (
                        `${sectionMark(
                          teams.map((e) => ({
                            cyclePercentage: e.cyclePercentage,
                            section: e.supervisorFeedBack,
                          })),
                        ).toFixed(2)}%`
                      )}
                    </Text>
                  </Space>
                </Row>
              )}
              {teams.find((y) => y.peerReviews !== undefined) !== undefined && (
                <Row style={{ marginBottom: 10 }}>
                  <Space size={20}>
                    <Row style={{ width: 300 }}>
                      <div style={{ width: 150 }}>
                        <Text>Peer Review</Text>
                      </div>
                    </Row>
                    <Text>
                      {teams.find((y) => typeof y.peerReviews?.answer === 'string') ? (
                        <Tag icon={<ClockCircleOutlined />} color="default">
                          Pending
                        </Tag>
                      ) : (
                        `${sectionMark(
                          teams.map((e) => ({
                            cyclePercentage: e.cyclePercentage,
                            section: e.peerReviews,
                          })),
                        ).toFixed(2)}%`
                      )}
                    </Text>
                  </Space>
                </Row>
              )}
              {teams.find((y) => y.internalCustomers !== undefined) !== undefined && (
                <Row style={{ marginBottom: 10 }}>
                  <Space size={20}>
                    <Row style={{ width: 300 }}>
                      <div style={{ width: 150 }}>
                        <Text>Internal Customer</Text>
                      </div>
                    </Row>
                    <Text>
                      {teams.find((y) => typeof y.internalCustomers?.answer === 'string') ? (
                        <Tag icon={<ClockCircleOutlined />} color="default">
                          Pending
                        </Tag>
                      ) : (
                        `${sectionMark(
                          teams.map((e) => ({
                            cyclePercentage: e.cyclePercentage,
                            section: e.internalCustomers,
                          })),
                        ).toFixed(2)}%`
                      )}
                    </Text>
                  </Space>
                </Row>
              )}
              {teams.find((y) => y.teamPerformance !== undefined) !== undefined && (
                <Row style={{ marginBottom: 10 }}>
                  <Space size={20}>
                    <Row style={{ width: 300 }}>
                      <div style={{ width: 150 }}>
                        <Text>Team Performance</Text>
                      </div>
                    </Row>
                    <Text>
                      {teams.find((y) => typeof y.teamPerformance?.answer === 'string') ? (
                        <Tag icon={<ClockCircleOutlined />} color="default">
                          Pending
                        </Tag>
                      ) : (
                        `${sectionMark(
                          teams.map((e) => ({
                            cyclePercentage: e.cyclePercentage,
                            section: e.teamPerformance,
                          })),
                        ).toFixed(2)}%`
                      )}
                    </Text>
                  </Space>
                </Row>
              )}
            </Col>
            {teams?.map((e, i) => (
              // eslint-disable-next-line react/jsx-key
              <Space key={i} direction={'vertical'} style={{ width: '100%' }} size={16}>
                <div
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', marginBottom: 10 }}>
                  <Row
                    style={{
                      alignItems: 'center',
                      marginTop: 10,
                      justifyContent: 'space-between',
                    }}>
                    <Space>
                      <ControlOutlined style={{ color: 'black', fontSize: 20 }} />
                      <Typography.Title level={5}>{e?.teamName}</Typography.Title>
                    </Space>
                    <Space>
                      <Typography.Title level={5}>
                        Allocated Percentage :- {e.cyclePercentage.toFixed(2)}%
                      </Typography.Title>
                    </Space>
                  </Row>
                  <Divider style={{ marginTop: 0, marginBottom: 20 }} />
                  <Table
                    columns={PerformanceResultsColumns}
                    dataSource={[
                      {
                        ...e?.supervisorFeedBack,
                        type: 'Supervisor FeedBack',
                        key: 'Supervisor FeedBack',
                        link: `/webux/employees/goals/user/${
                          user?.goalHolderId
                        }/${encodeURIComponent(user?.userName ?? '')}/${user?.id}/${
                          e?.teamId
                        }?cycle=${cycle}`,
                      },
                      {
                        ...e?.peerReviews,
                        type: 'Peer Reviews',
                        key: 'Peer Reviews',
                        link: `/webux/employees/${user?.id}/${encodeURIComponent(
                          user?.userName ?? '',
                        )}/peer-reviews?cycle=${cycle}`,
                      },
                      {
                        ...e?.internalCustomers,
                        type: 'Internal Customers',
                        key: 'Internal Customers',
                        link: `/webux/employees/${e?.teamId}/${encodeURIComponent(
                          e?.teamName ?? '',
                        )}/internal-customers?cycle=${cycle}`,
                      },
                      {
                        ...e?.teamPerformance,
                        type: 'Team Performance',
                        key: 'Team Performance',
                        link: `/webux/employees/goals/team/${e?.goalHolderId}/${encodeURIComponent(
                          e?.teamName ?? '',
                        )}/${e?.teamId}?cycle=${cycle}`,
                      },
                    ].filter((e1) => e1.answer !== undefined)}
                    expandable={{}}
                    indentSize={70}
                    pagination={false}
                    size={'small'}
                    // summary={() => {
                    //   return (
                    //     <>
                    //       <Table.Summary.Row>
                    //         <Table.Summary.Cell colSpan={6} index={1} />
                    //         <Table.Summary.Cell index={2} colSpan={1}>
                    //           Total
                    //         </Table.Summary.Cell>
                    //         <Table.Summary.Cell index={3}>
                    //           <div
                    //             style={{
                    //               width: '50%',
                    //               display: 'flex',
                    //               justifyContent: 'flex-start',
                    //             }}>
                    //             <Typography>5</Typography>
                    //           </div>
                    //         </Table.Summary.Cell>
                    //       </Table.Summary.Row>
                    //     </>
                    //   );
                    // }}
                  />
                </div>
              </Space>
            ))}
          </Spin>
        </WebUxPageItem>
      </WebUxPageContent>
    </WebUxPage>
  );
};
